"use client";

import { useAuth } from "@/hooks/use-auth";
import { getRelationshipFromUrl } from "@/hooks/use-group-switch";
import { getDefaultCountry, type Country } from "@/info/countries";
import { formatPhoneNumber, isValidOtp, isValidPhoneNumber, type LoginStep, type OtpMethod } from "@/info/login";
import { useUserStore } from "@/stores/user-store";
import { logger } from "@/utils/logger";
import { setUserPreferences } from "@/utils/user-preferences";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

interface UseLoginFormProps {
	onLoginSuccess?: () => void;
	setIsOpen?: (open: boolean) => void;
	isShort?: boolean;
}

export function useLoginForm({ onLoginSuccess, setIsOpen, isShort }: UseLoginFormProps = {}) {
	const [step, setStep] = useState<LoginStep>("phone");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [otp, setOtp] = useState("");
	const [selectedCountry, setSelectedCountry] = useState<Country>(getDefaultCountry());
	const [otpMethod, setOtpMethod] = useState<OtpMethod>(getDefaultCountry().countryCode === "IN" ? "sms" : "whatsapp");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const [otpSentAt, setOtpSentAt] = useState<number | null>(null);
	const [showRelationshipGuard, setShowRelationshipGuard] = useState(false);
	const { sendOtp, login, user } = useAuth();
	const pathname = usePathname();
	const setSelectedGroup = useUserStore(state => state.setSelectedGroup);

	// Update OTP method when country changes
	useEffect(() => {
		setOtpMethod(selectedCountry.countryCode === "IN" ? "sms" : "whatsapp");
	}, [selectedCountry]);

	const resetForm = () => {
		setStep("phone");
		setPhoneNumber("");
		setOtp("");
		setError("");
		setIsLoading(false);
		setOtpSentAt(null);
	};

	const handleSendOTP = async () => {
		if (isLoading) return;

		if (!phoneNumber || !isValidPhoneNumber(phoneNumber, selectedCountry.code)) {
			setError("Please enter a valid phone number");
			return;
		}

		setIsLoading(true);
		setError("");

		try {
			const fullPhoneNumber = formatPhoneNumber(phoneNumber, selectedCountry.code);
			const success = await sendOtp(fullPhoneNumber, false, otpMethod);
			if (!success) {
				throw new Error("Failed to send OTP");
			}
			setStep("otp");
			setOtpSentAt(Date.now());
		} catch (error: unknown) {
			if (logger) {
				setError("Failed to send OTP. Please try again.");
			} else {
				setError("Failed to send OTP. Please try again." + (error as Error).message);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleResendOTP = async () => {
		if (isLoading) return;

		setIsLoading(true);
		setError("");

		try {
			const fullPhoneNumber = formatPhoneNumber(phoneNumber, selectedCountry.code);
			const success = await sendOtp(fullPhoneNumber, true, otpMethod);
			if (!success) {
				throw new Error("Failed to resend OTP");
			}
			setOtpSentAt(Date.now());
		} catch (error: unknown) {
			if (logger) {
				logger.warn("Resend OTP error:", error);
				setError("Failed to resend OTP. Please try again.");
			} else {
				setError("Failed to resend OTP. Please try again." + (error as Error).message);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleVerifyOTP = async () => {
		if (isLoading) return;

		if (!otp || !isValidOtp(otp)) {
			setError("Please enter a valid 5-digit OTP");
			return;
		}

		setIsLoading(true);
		setError("");

		try {
			const fullPhoneNumber = formatPhoneNumber(phoneNumber, selectedCountry.code);
			const loginResult = await login(fullPhoneNumber, otp);

			if (!loginResult) {
				throw new Error("Invalid OTP");
			}

			// Check if user should see relationship selection dialog
			// Show it if: no groups at all, OR has both buyer and seller groups
			// Use loginResult.user to ensure we have the freshly logged-in user data
			const userGroups = loginResult.user?.groups || [];
			const hasBuyerGroup = userGroups.some((g: { relationship: string }) => g.relationship === "buyer");
			const hasSellerGroup = userGroups.some((g: { relationship: string }) => g.relationship === "seller");
			const shouldShowRelationshipDialog = userGroups.length === 0 || (hasBuyerGroup && hasSellerGroup);

			// Check if URL indicates a relationship (e.g., /sell/*, /dashboard/seller/*)
			// If so, auto-set the relationship and skip the dialog
			const urlRelationship = getRelationshipFromUrl(pathname);
			if (urlRelationship) {
				// URL indicates relationship - auto-set and skip dialog
				setUserPreferences({ relationship: urlRelationship });
				setSelectedGroup({ relationship: urlRelationship });
				setIsOpen?.(false);
				onLoginSuccess?.();
			} else if (shouldShowRelationshipDialog) {
				// Close login dialog first
				setIsOpen?.(false);
				// Show relationship guard to let user pick their role
				setShowRelationshipGuard(true);
			} else {
				// User has only buyer or only seller groups - proceed normally
				setIsOpen?.(false);
				onLoginSuccess?.();
			}

			isShort && setStep("verified");
		} catch (error: unknown) {
			if (logger) {
				logger.warn("Verify OTP error:", error);
				setError("Invalid OTP. Please try again.");
			} else {
				setError("Invalid OTP. Please try again." + (error as Error).message);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleBackToPhone = () => {
		setStep("phone");
		setOtp("");
		setError("");
	};

	const handleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			if (step === "phone") {
				handleSendOTP();
			} else if (step === "otp") {
				handleVerifyOTP();
			}
		}
	};

	return {
		// State
		step,
		phoneNumber,
		otp,
		selectedCountry,
		otpMethod,
		isLoading,
		error,
		otpSentAt,
		showRelationshipGuard,
		// Setters
		setPhoneNumber,
		setOtp,
		setSelectedCountry,
		setOtpMethod,
		setShowRelationshipGuard,
		// Handlers
		handleSendOTP,
		handleResendOTP,
		handleVerifyOTP,
		handleBackToPhone,
		handleKeyPress,
		resetForm,
		// User for relationship guard
		currentUser: user,
		// Callback for after relationship is selected
		onRelationshipSelected: () => {
			setShowRelationshipGuard(false);
			onLoginSuccess?.();
		},
	};
}
